import React, { useState, useEffect } from 'react';
import { Button, DatePicker, Input, Select, Typography } from 'antd';
import moment from 'moment';
import axios from 'axios';
import { API_BASE_URL } from '../../../../../api';
import { useSelector } from "react-redux";
import Sidebar from "../../GlobalFiles/Sidebar";
import { toast } from "react-toastify";
import exportCollectionToExcel from '../../../../../helper/exportCollectionToXLS';
import printCollectionReport from '../../../../../helper/printCollectionReport';
import { getSuperGroupName } from '../../../../../utils/getSuperGroupName';

const { Title, Text } = Typography;


const MonthlyCollection = () => {
  const [loading, setLoading] = useState(false);
  const [collectionData, setCollectionData] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(moment().month());
  const [selectedYear, setSelectedYear] = useState(moment().year());
  const [searchByIdValue, setSearchByIdValue] = useState('');

  const [excludeOrders, setExcludeOrders] = useState({ ctb: false, ref: false, free: false, self: false, due: false, nct: false });

  const { data: { user }, } = useSelector((state) => state.auth);
  const isManager = user?.userType === "admin";

  const formatAmount = (val) => {
    if (val === null || val === undefined) return '0.00';
    const n = Number(val);
    return Number.isFinite(n) ? n.toFixed(2) : '0.00';
  };

  const getSelectedMoment = React.useCallback((m = selectedMonth, y = selectedYear) => {
    return moment.utc({ year: y, month: m, day: 1 });
  }, [selectedMonth, selectedYear]);

  const fetchDailyCollection = React.useCallback(async (monthOverride = null, yearOverride = null) => {
    const activeMonth = monthOverride !== null && monthOverride !== undefined ? monthOverride : selectedMonth;
    const activeYear = yearOverride !== null && yearOverride !== undefined ? yearOverride : selectedYear;
    console.log('Selections:', { activeMonth, activeYear });
    if (activeMonth === null || activeMonth === undefined || activeYear === null || activeYear === undefined) return;

    setLoading(true);
    try {
      const monthStartUtc = moment({ year: activeYear, month: activeMonth, day: 1 })
        .startOf('month')
        .utc()
        .toISOString();
      const monthEndUtc = moment({ year: activeYear, month: activeMonth, day: 1 })
        .endOf('month')
        .utc()
        .toISOString();
      console.log('Fetching monthly collection range (UTC):', monthStartUtc, monthEndUtc);
      const response = await axios.get(`${API_BASE_URL}/testorders/collection`, {
        params: {
          startUtc: monthStartUtc,
          endUtc: monthEndUtc,
        },
      });
      console.log('Monthly collection response:', response.data?.orders);
      setCollectionData(response.data);
    } catch (error) {
      console.error('Error fetching monthly collection:', error);
      console.error('Error response:', error.response?.data);
      toast.error('Failed to fetch monthly collection data');
    } finally {
      setLoading(false);
    }
  }, [getSelectedMoment, selectedMonth, selectedYear]);

  useEffect(() => {
    if (isManager) {
      fetchDailyCollection();
    }
  }, [isManager, fetchDailyCollection, getSelectedMoment]);

  useEffect(() => {
    fetchDailyCollection();
  }, [selectedMonth, selectedYear, fetchDailyCollection]);


  const orders = React.useMemo(() => (
    collectionData ? collectionData.orders.filter(order => {
      console.log("Order: ", order);
      if (excludeOrders.ctb && order.paymentType === 'ctb') return false;
      if (excludeOrders.ref && (order.paymentType === 'ref' || order.paymentType === 'referred')) return false;
      if (excludeOrders.free && order.paymentType === 'free') return false;
      if (excludeOrders.self && order.paymentType === 'self') return false;
      if (excludeOrders.due && !order.isPaid) return false;
      if (excludeOrders.nct && order.paymentType === 'nct') return false;
      if (searchByIdValue && !(order.testOrderID?.toString()?.toLocaleLowerCase() === searchByIdValue.toLocaleLowerCase())) return false;
      return true;
    }) : []
  ), [collectionData, excludeOrders, searchByIdValue]);


  const exportToExcel = () => {
    if (!collectionData) return;
    const sgs = collectionData.superGroups || [];
    exportCollectionToExcel(sgs, orders);
  };

  const handlePrint = () => {
    if (!collectionData) return;
    const sgs = collectionData.superGroups || [];
    printCollectionReport(sgs, orders);
  };

  const calculateTotal = React.useCallback((superGroup) => {
    if (!collectionData) return 0;
    let total = 0;
    let groupTotal = {};
    orders.forEach(order => {
      const tests = order.tests || {};
      // console.log("Tests: ", tests);
      Object.entries(tests).forEach(([sg, amount]) => {
        if (!groupTotal[sg]) groupTotal[sg] = 0;
        groupTotal[sg] += amount || 0;
        total += amount || 0;
        // console.log(`Order ID: ${order.testOrderID}, SG: ${sg}, Amount: ${amount}`);
        // console.log(amount);
      });
    });
    return superGroup ? (groupTotal[superGroup] || 0) : total;
  }, [collectionData, orders]);

  useEffect(() => {
    console.log("Total: ", calculateTotal());
  }, [collectionData, orders, calculateTotal]);


  if (!isManager) {
    return (
      <div className="flex min-h-screen bg-gray-100">
        <Sidebar />
        <div className="flex-1 p-4 md:p-8 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600">Only managers can access this page.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex min-h-screen bg-gray-50 max-w-screen overflow-x-hidden">
        <Sidebar onCollapse={setSidebarCollapsed} />
        <div className={`flex-1 min-w-0 p-6 transition-all duration-300 ${sidebarCollapsed ? 'ml-15' : 'ml-0'}`}>
          <div className="flex-1 min-w-0 p-6 ml-20">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-3 gap-4 bg-gradient-to-r from-sky-600 to-indigo-500 p-6 rounded-lg">
              <h1 className="text-3xl font-bold text-gray-200 flex items-center">
                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Monthly Collection Report
              </h1>
              <div className="flex flex-col md:flex-row md:items-center gap-2 md:space-x-4">
                <Button
                  type="primary"
                  onClick={() => fetchDailyCollection()}
                  className="px-4 py-2 bg-gray-100 text-black rounded-md hover:bg-gray-300 transition-colors flex items-center whitespace-nowrap"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Refresh
                </Button>
                <Select
                  value={selectedMonth}
                  onChange={(value) => {
                    setSelectedMonth(value);
                    fetchDailyCollection(value, selectedYear);
                  }}
                  style={{ width: 160 }}
                  className="rounded-md"
                >
                  <Select.Option value={null} disabled>Select Month</Select.Option>
                  {moment.months().map((name, idx) => (
                    <Select.Option key={idx} value={idx}>{name}</Select.Option>
                  ))}
                </Select>

                <Select
                  value={selectedYear}
                  onChange={(value) => {
                    setSelectedYear(value);
                    fetchDailyCollection(selectedMonth, value);
                  }}
                  style={{ width: 120 }}
                  className="rounded-md"
                >
                  <Select.Option value={null} disabled>Select Year</Select.Option>
                  {Array.from({ length: 201 }).map((_, i) => {
                    const y = 2020 + i; // years 2020..4020
                    return <Select.Option key={y} value={y}>{y}</Select.Option>;
                  })}
                </Select>
                <button
                  onClick={handlePrint}
                  disabled={!collectionData}
                  aria-label="Print report"
                  className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors flex items-center disabled:opacity-60 whitespace-nowrap"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  Print Report
                </button>
                <button
                  onClick={exportToExcel}
                  disabled={!collectionData}
                  aria-label="Export to Excel"
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center disabled:opacity-60 whitespace-nowrap"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Export to Excel
                </button>
              </div>
            </div>

            <div className='flex justify-between items-center p-3 bg-gradient-to-r from-blue-100 to-gray-100 rounded-tl-lg rounded-tr-lg mb-0'>
              <Title level={4} className="m-0">Selection: {selectedMonth !== null && selectedYear ? getSelectedMoment().format('MMMM YYYY') : 'No date selected'}</Title>
              <Input
                placeholder="Search by Test Order ID"
                value={searchByIdValue}
                onChange={(e) => setSearchByIdValue(e.target.value)}
                style={{ width: 400 }}
                className='p-2 text-black'
                allowClear
              />
            </div>

            {loading && (
              <div className="flex min-h-screen bg-gray-50">
                <Sidebar onCollapse={setSidebarCollapsed} />
                <div className={`flex-1 p-6 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0'}`}>
                  <div className="flex-1 p-6 ml-40">
                    <div className="text-center p-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
                      <p className="mt-4 text-gray-600">Loading daily collection data...</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {(!collectionData) && (
              (!loading && (selectedMonth === null || selectedYear === null)) ? (
                <div className="flex min-h-screen bg-gray-50">
                  <Sidebar onCollapse={setSidebarCollapsed} />
                  <div className={`flex-1 p-6 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0'}`}>
                    <div className="flex-1 p-6 ml-40">
                      <div className="text-center p-12">
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Month and Year are not selected</h2>
                        <p className="text-gray-600">Please Select a month and year to see collections.</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex min-h-screen bg-gray-50">
                  <Sidebar onCollapse={setSidebarCollapsed} />
                  <div className={`flex-1 p-6 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0'}`}>
                    <div className="flex-1 p-6 ml-40">
                      <div className="text-center p-12">
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">No Data Available</h2>
                        <p className="text-gray-600">No collection data found for the selected date.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )
            )}

            {collectionData && (
              <div className="bg-white rounded-lg shadow-md p-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="flex items-center">
                      <svg className="w-8 h-8 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-blue-600">Total Orders</p>
                        <p className="text-2xl font-bold text-blue-900">{collectionData?.orders?.length || 0}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="flex items-center">
                      <svg className="w-8 h-8 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-green-600">Total Amount</p>
                        <p className="text-2xl font-bold text-green-900">৳{formatAmount(calculateTotal())}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-emerald-50 p-4 rounded-lg">
                    <div className="flex items-center">
                      <svg className="w-8 h-8 text-emerald-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-emerald-600">Total Paid</p>
                        <p className="text-2xl font-bold text-emerald-900">{orders?.filter(item => item.isPaid).length || 0}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-red-50 p-4 rounded-lg">
                    <div className="flex items-center">
                      <svg className="w-8 h-8 text-red-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-red-600">Total Due</p>
                        <p className="text-2xl font-bold text-red-900">{orders?.filter(item => !item.isPaid).length || 0}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-8 w-full overflow-x-auto">
                  <div className='flex justify-between items-center my-3'>
                    <h3 className="text-lg font-semibold text-gray-900">Orders Details</h3>
                    <div className="flex items-center justify-end gap-2">
                      <Button type={!excludeOrders.ctb ? "default" : "primary"} onClick={() => { setExcludeOrders({ ...excludeOrders, ctb: !excludeOrders.ctb }); }}>{excludeOrders.ctb ? "Unclear CTB" : "Clear CTB"}</Button>
                      <Button type={!excludeOrders.ref ? "default" : "primary"} onClick={() => { setExcludeOrders({ ...excludeOrders, ref: !excludeOrders.ref }); }}>{excludeOrders.ref ? "Unclear REF" : "Clear REF"}</Button>
                      <Button type={!excludeOrders.free ? "default" : "primary"} onClick={() => { setExcludeOrders({ ...excludeOrders, free: !excludeOrders.free }); }}>{excludeOrders.free ? "Unclear FREE" : "Clear FREE"}</Button>
                      <Button type={!excludeOrders.self ? "default" : "primary"} onClick={() => { setExcludeOrders({ ...excludeOrders, self: !excludeOrders.self }); }}>{excludeOrders.self ? "Unclear SELF" : "Clear SELF"}</Button>
                      <Button type={!excludeOrders.due ? "default" : "primary"} onClick={() => { setExcludeOrders({ ...excludeOrders, due: !excludeOrders.due }); }}>{excludeOrders.due ? "Unclear DUE" : "Clear DUE"}</Button>
                      <Button type={!excludeOrders.nct ? "default" : "primary"} onClick={() => { setExcludeOrders({ ...excludeOrders, nct: !excludeOrders.nct }); }}>{excludeOrders.nct ? "Unclear NCT" : "Clear NCT"}</Button>
                    </div>
                  </div>
                  <div className="w-full overflow-x-auto">
                    <table className="w-full min-w-[900px] table-auto border-collapse">
                      <thead>
                        <tr className="bg-sky-200 text-sky-900 font-semibold">
                          <th className="p-3 text-left border-b whitespace-nowrap  min-w-20">Date</th>
                          <th className="p-3 text-left border-b whitespace-nowrap">Order ID</th>
                          <th className="p-3 text-left border-b whitespace-nowrap">Doctor Name</th>
                          <th className="p-3 text-left border-b max-w-xs">Remarks</th>

                          {collectionData?.superGroups && collectionData.superGroups.map((sg, idx) => (
                            <th key={idx} className="p-3 text-left border-b max-w-[120px] truncate" title={getSuperGroupName(sg)}>{getSuperGroupName(sg)}</th>
                          ))}

                          <th className="p-3 text-left border-b whitespace-nowrap">Total</th>
                          <th className="p-3 text-left border-b whitespace-nowrap">Advance</th>
                          <th className="p-3 text-left border-b whitespace-nowrap">Due</th>
                          <th className="p-3 text-left border-b whitespace-nowrap">Less</th>
                          <th className="p-3 text-left border-b whitespace-nowrap">End Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((order, index) => (
                          <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-200/80"}>
                            <td className="p-3 border-b border-gray-200 whitespace-nowrap">{order.createdAt ? new Date(order.createdAt).toLocaleDateString() : '-'}</td>
                            <td className="p-3 border-b border-gray-200 whitespace-nowrap">{order.testOrderID || 'N/A'}</td>
                            <td className="p-3 border-b border-gray-200 whitespace-nowrap">{order.doctor?.docName || '-'}</td>
                            <td className="p-3 border-b border-gray-200 max-w-xs break-words">{order.paymentType || '-'}</td>
                            {collectionData?.superGroups && collectionData.superGroups.map((sg, idx) => (
                              <td key={idx} className="p-3 border-b border-gray-200 max-w-[120px] text-center whitespace-nowrap" title={getSuperGroupName(sg)}>
                                {order.tests && order.tests[sg] ? `৳${formatAmount(order.tests[sg])}` : '৳0.00'}
                              </td>
                            ))}
                            <td className="p-3 border-b border-gray-200 whitespace-nowrap">৳{formatAmount(
                              order.tests ? Object.values(order.tests).reduce((sum, test) => sum + (test || 0), 0) : 0
                            )}</td>
                            <td className="p-3 border-b border-gray-200 whitespace-nowrap">৳{formatAmount(order.realPaidAmount)}</td>
                            <td className="p-3 border-b border-gray-200 whitespace-nowrap">৳{formatAmount(order.realDueAmount)}</td>
                            <td className="p-3 border-b border-gray-200 whitespace-nowrap">৳{formatAmount(order.realDiscountAmount)}</td>
                            <td className="p-3 border-b border-gray-200 whitespace-nowrap">{order.lastPaymentDate ? new Date(order.lastPaymentDate).toLocaleDateString() : '-'}</td>
                          </tr>
                        ))}

                        {(!orders || orders.length === 0) && (
                          <tr>
                            <td className="p-3 py-6 border-b border-gray-200 text-center font-bold" colSpan={9 + (collectionData?.superGroups?.length || 0)}>No orders found for the selected date.</td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot>
                        <tr className="bg-slate-100 text-slate-900 font-bold">
                          <td className="p-3" colSpan={4}>Total ({collectionData?.orders?.length || 0} Test Orders)</td>
                          {collectionData?.superGroups && collectionData.superGroups.map((sg, idx) => (
                            <td key={idx} className="p-3 text-center whitespace-nowrap">৳{formatAmount(calculateTotal(sg))}</td>
                          ))}
                          <td className="p-3 whitespace-nowrap">৳{formatAmount(calculateTotal())}</td>
                          <td className="p-3 whitespace-nowrap">৳{formatAmount(orders.reduce((acc, order) => acc + (Number(order.realPaidAmount) || 0), 0))}</td>
                          <td className="p-3 whitespace-nowrap">৳{formatAmount(orders.reduce((acc, order) => acc + (Number(order.realDueAmount) || 0), 0))}</td>
                          <td className="p-3 whitespace-nowrap">৳{formatAmount(orders.reduce((acc, order) => acc + (Number(order.realDiscountAmount) || 0), 0))}</td>
                          <td className="p-3" colSpan="1"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default MonthlyCollection;
